/** EMP-WS-UI-001 — premium form primitives (same testids ws-<key> / ws-error-<key> as before). */
import React from 'react';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Lock } from 'lucide-react';
import { RoleSelect } from '../RoleSelect';

export const FieldLabel = ({ label, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="text-[13px] font-medium text-slate-700 mb-1.5 block">
    {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
  </label>
);

const ctl = (error) => `ews-control h-10 rounded-lg border-slate-200 bg-white text-[14px] ${error ? 'border-rose-300 focus-visible:ring-rose-200' : 'focus-visible:ring-violet-200 focus-visible:border-violet-300'}`;

export const PremiumField = ({ field, value, error, onChange, options }) => {
  const opts = field.options ? field.options.map(o => ({ value: o, label: o })) : (options[field.optionsKey] || []);
  return (
    <div className={field.full ? 'sm:col-span-2' : ''}>
      <FieldLabel label={field.label} required={field.required} />
      {field.type === 'role' ? (
        <RoleSelect value={value || ''} onChange={(v) => onChange(field.key, v)} options={opts} error={error} testId={`ws-${field.key}`} />
      ) : field.type === 'select' ? (
        <Select value={value ?? ''} /* HR-SHIFT-VALIDATION-001: always controlled; '' ⇒ placeholder */ onValueChange={(v) => onChange(field.key, v)}>
          <SelectTrigger data-testid={`ws-${field.key}`} className={ctl(error)}>
            <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : field.type === 'textarea' ? (
        <Textarea value={value || ''} rows={2} data-testid={`ws-${field.key}`}
          onChange={(e) => onChange(field.key, e.target.value)} className={`ews-control rounded-lg border-slate-200 text-[14px] resize-none ${error ? 'border-rose-300' : 'focus-visible:ring-violet-200'}`} />
      ) : (
        <Input type={field.type === 'date' ? 'date' : 'text'} value={value || ''} data-testid={`ws-${field.key}`}
          onChange={(e) => onChange(field.key, e.target.value)} className={ctl(error)} />
      )}
      {error && <p className="text-[12px] text-rose-600 mt-1 ews-fade" data-testid={`ws-error-${field.key}`}>{error}</p>}
    </div>
  );
};

/** Immutable field with an optional audited action button (Edit Employee ID / Edit Work Email). */
export const ReadOnlyField = ({ field, value, hint, action }) => (
  <div>
    <FieldLabel label={field.label} />
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <Input value={value || ''} disabled data-testid={`ws-${field.key}`} className="h-10 rounded-lg border-slate-200 bg-slate-50 text-slate-600 pr-8 disabled:opacity-100" />
        <Lock className="h-3.5 w-3.5 text-slate-300 absolute right-3 top-1/2 -translate-y-1/2" />
      </div>
      {action}
    </div>
    <p className="text-[12px] text-slate-400 mt-1">{hint}</p>
  </div>
);
